// Import the stylesheet through the bundler (not a static <link>) so Vite
// HMR hot-patches CSS edits instantly in dev and serves it uncached.
import './style.css';
import { AdminClient, AdminApiError, type ApprovalView, type IntegratorSummary } from '@apx/admin-sdk';
import { timingSchema, type TimingOverride } from '@apx/integrator-config/schemas';
import { renderApprovalsPage, type ApprovalsPageDeps } from './pages/approvals.js';
import { renderProviderApprovalsPage } from './pages/approvals-provider.js';
import { renderBulkConfigPage } from './pages/bulk-config.js';
import { renderPlayersPage, type PlayersPageDeps } from './pages/players.js';
import { renderGamesIndexPage } from './pages/games.js';
import { renderGameDetailPage, parseTabFromHash } from './pages/game-detail.js';
import { renderProviderGamesPage } from './pages/provider-games.js';
import { renderActivityPage, type ActivityPageDeps } from './pages/activity.js';
import { renderIntegratorsPage, type IntegratorsPageDeps } from './pages/integrators.js';
import { renderAuditPage, type AuditPageDeps } from './pages/audit.js';
import { renderAuditChainsPage, type AuditChainsPageDeps } from './pages/audit-chains.js';
import { renderTransactionTracePage, type TransactionTraceDeps } from './pages/transaction-trace.js';
import { renderAuditAnchorsPage, type AuditAnchorsPageDeps } from './pages/audit-anchors.js';
import { renderRoundsExplorerPage, type RoundsExplorerDeps } from './pages/rounds-explorer.js';
import { renderReportsPage, type ReportsPageDeps, type ReportTab } from './pages/reports.js';
import { renderReportGameDetailPage } from './pages/report-game-detail.js';
import { renderApiKeysPage, type ApiKeysPageDeps } from './pages/api-keys.js';
// Game catalog merged into Games page (Phase: sidebar consolidation).
import { renderWebhooksPage, type WebhooksPageDeps } from './pages/webhooks.js';
import { renderHomePage, type HomePageDeps } from './pages/home.js';
import { renderOverviewPage, type OverviewPageDeps } from './pages/overview.js';
import { renderProviderReportPage, type ProviderReportDeps, type ProviderReportView } from './pages/reports-provider.js';
import { renderPlayerDetailPage, type PlayerDetailPageDeps } from './pages/player-detail.js';
import { renderIntegratorDetailPage, type IntegratorDetailPageDeps } from './pages/integrator-detail.js';
import { renderWalletLedgerPage, type WalletLedgerPageDeps } from './pages/wallet-ledger.js';
import { renderWalletDriftPage, type WalletDriftPageDeps } from './pages/wallet-drift.js';
import { renderInspectorAllPage, type InspectorAllPageDeps } from './pages/inspector-all.js';
import { renderJackpotOverviewPage, type JackpotOverviewDeps } from './pages/jackpot-overview.js';
import { renderAdminUsersPage, type AdminUsersPageDeps } from './pages/admin-users.js';
import { renderCurrenciesPage } from './pages/currencies.js';
import { renderSystemHealthPage, type SystemHealthPageDeps } from './pages/system-health.js';
import {
  renderRedisMonitoringPage,
  type RedisMonitoringPageDeps,
} from './pages/redis-monitoring.js';
import { renderMaintenancePage, type MaintenancePageDeps } from './pages/maintenance.js';
import { renderRoundOpsPage, type RoundOpsPageDeps } from './pages/round-ops.js';
import { renderTenantStaffPage } from './pages/tenant-staff.js';
import { currentPath, installNavInterceptor, navigate, onRouteChange } from './nav.js';
import { installPalette } from './palette.js';
import { installShortcuts } from './shortcuts.js';
import { installSidebarCollapse } from './sidebar-collapse.js';
import { installIntegratorPicker, pushRecentIntegrator, type IntegratorPickerHandle } from './integrator-picker.js';
import { installTheme } from './theme.js';
import { icon } from './icons.js';
import { deriveScope, type Scope } from './scope.js';

const SELECTED_TENANT_KEY = 'apx-admin:integrator';

// Global maintenance is a pre-deploy kill switch that's easy to leave engaged
// after health goes green — which silently parks the round scheduler for every
// game. Once it's been on longer than this, nag on every page with a deeplink
// to lift it. 15 min clears any normal deploy window without crying wolf.
const MAINTENANCE_WARN_AFTER_MIN = 15;
const MAINTENANCE_POLL_MS = 30_000;

// API base + service URLs come from build-time Vite env (baked into the
// dist bundle). Dev fallback is the same-origin path the Vite dev proxy
// rewrites to the local admin-api. In prod, Dockerfile.web sets these to
// absolute https://api.${BASE_DOMAIN} (clean, no /admin or /api prefix on
// the external URL — Caddy rewrites HMAC routes back to admin-api's
// internal /api/v1/* paths transparently).
const API_BASE = import.meta.env.VITE_ADMIN_API_BASE ?? '/api/admin';
const ROUND_ENGINE_BASE = import.meta.env.VITE_ROUND_ENGINE_BASE ?? '/api/round-engine';
// ALWAYS same-origin: socket.io dials `<origin>/socket.io/`, which Caddy
// reverse-proxies to gateway:53000 on every deploy (admin vhost), and local dev
// resolves the same path via the vite /socket.io ws-proxy.
//
// We deliberately do NOT honour VITE_GATEWAY_URL here: that build arg is baked
// to the *games'* gateway origin (e.g. https://play.club78.net) because the
// game iframes run on play.* and connect same-origin. The admin SPA runs on
// admin.* — using play.* would make the Live Inspector's WebSocket cross-origin,
// which the admin page's CSP `connect-src 'self'` blocks → "websocket error".
// Staying on window.location.origin keeps it same-origin and CSP-clean.
const GATEWAY_URL = window.location.origin;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const pickerMount = document.getElementById('integrator-picker-mount');
if (!pickerMount) throw new Error('Missing #integrator-picker-mount in index.html');
const integratorPicker: IntegratorPickerHandle = installIntegratorPicker(pickerMount, {
  onSelectProvider: () => {
    // On a game-config page, "Provider · all integrators" means edit the
    // baseline in place — switch to provider scope (empty value) and re-render
    // the current page. Elsewhere, the provider entry opens the provider overview.
    if (currentPath().startsWith('/games/')) {
      integratorPicker.setValue('');
      els.integratorPicker.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      navigate('/');
    }
  },
});

const els = {
  topbar: document.querySelector<HTMLElement>('.topbar')!,
  shell: document.querySelector<HTMLElement>('.shell')!,
  content: document.getElementById('content')!,
  integratorPicker: integratorPicker.select,
  who: document.getElementById('who')!,
  logout: document.getElementById('logout') as HTMLButtonElement,
  acct: document.getElementById('acct')!,
  acctToggle: document.getElementById('acct-toggle') as HTMLButtonElement,
  acctMenu: document.getElementById('acct-menu')!,
  acctAvatar: document.getElementById('acct-avatar')!,
  acctAvatarLg: document.getElementById('acct-avatar-lg')!,
  maintBanner: document.getElementById('maint-banner')!,
  login: document.getElementById('login')!,
  loginEmail: document.getElementById('login-email') as HTMLInputElement,
  loginPassword: document.getElementById('login-password') as HTMLInputElement,
  loginSubmit: document.getElementById('login-submit') as HTMLButtonElement,
  loginPwToggle: document.getElementById('login-pw-toggle') as HTMLButtonElement,
  loginError: document.getElementById('login-error')!,
  loginBanner: document.getElementById('login-banner')!,
};

let client: AdminClient | null = null;
let integrators: IntegratorSummary[] = [];

// C12 — the admin JWT rides an HttpOnly cookie the SDK sends via
// `credentials: 'include'`; it is NOT stored in JS.

// Effective identity + permissions from GET /v1/me — the single source of
// truth for sidebar/permission gating (was a client-side JWT decode + a
// hardcoded role→perm map that could drift from the server).
interface AdminSession {
  userId: string;
  email: string | null;
  roles: string[];
  permissions: Set<string>;
  isSuperAdmin: boolean;
  /**
   * Tenant staff, confined to their own integrator(s). Comes from the server:
   * capability-only accounts have no roles, so the client cannot infer it.
   */
  isLocked: boolean;
  tenantLock: string[];
}
let session: AdminSession | null = null;
// True only after a successful authentication this page-load. Gates the
// "session expired" banner so it shows on a genuine mid-session bounce, not on
// a cold load / fresh logout where the /v1/me probe simply 401s.
let hasAuthenticated = false;

function principalHasPermission(perm: string): boolean {
  if (!session) return false;
  return session.isSuperAdmin || session.permissions.has(perm);
}

// Sidebar route → permission required to see it. Routes not listed are always visible.
const SIDEBAR_GUARDS: Record<string, string> = {
  staff: 'tenant_user.manage',
  integrators: "integrator.read",
  'admin-users': 'admin_user.manage',
  currencies: 'game.config.read',
  'system-health': 'audit.read',
  maintenance: 'game.config.write',
  players: 'player.read',
  'game-catalog': 'integrator.read',
  'api-keys': 'api_key.write',
  webhooks: 'webhook.write',
  games: 'game.config.read',
  'round-ops': 'round.force_finish',
  'rounds-explorer': 'audit.read',
  'wallet-ledger': 'report.financial.read',
  'wallet-drift': 'report.financial.read',
  'inspector-all': 'audit.read',
  reports: 'report.financial.read',
  'reports-pl': 'report.financial.read',
  'reports-product-pl': 'report.financial.read',
  'reports-player-pl': 'report.financial.read',
  'reports-game-log': 'report.activity.read',
  'reports-retention': 'report.activity.read',
  // Exposes per-transaction money, so it sits behind the financial grant
  // rather than the lighter activity one.
  'reports-reconcile': 'report.financial.read',
  'jackpot-overview': 'report.financial.read',
  'reports-by-integrator': 'report.financial.read',
  'reports-by-game': 'report.financial.read',
  'reports-by-player': 'report.financial.read',
  'incomplete-games': 'audit.read',
  approvals: 'game.config.write',
  // Provider-wide queue: the list endpoint needs config read; deciding a row
  // still checks its own capability server-side.
  'approvals-all': 'game.config.read',
  // Bulk proposals follow the surface's capability; fairness covers all but
  // maintenance (game.pause), and every batch decision is super-admin anyway.
  'bulk-config': 'game.fairness.write',
  audit: 'audit.read',
  'transaction-trace': 'audit.read',
};

/**
 * Routes gated on IDENTITY, not on a capability.
 *
 * SIDEBAR_GUARDS cannot express "super admin only": principalHasPermission is
 * `isSuperAdmin || permissions.has(perm)`, an AT-LEAST check, so any permission
 * string it is given is also satisfied by whichever role happens to hold that
 * permission. Redis monitoring exposes raw infrastructure state, so it is the
 * principal that opens it, not a grant.
 *
 * Cosmetic, like the rest of the sidebar gating — the server is the boundary.
 */
const SUPERADMIN_ROUTES: ReadonlySet<string> = new Set(['redis-monitoring']);

function updateTopbarBadges(): void {
  const roles = session?.roles ?? [];
  // Name the tenant in the topbar for locked staff. Without it the chrome reads
  // identically to the provider console, and an integrator admin has no way to
  // tell whose data they are looking at — the slug chip alone is easy to miss.
  const tenantTitle = document.getElementById('tenant-title');
  const brandTag = document.getElementById('brand-tag');
  if (tenantTitle && brandTag) {
    const lockedTo = session?.isLocked ? (session.tenantLock[0] ?? null) : null;
    const own = lockedTo ? integrators.find((t) => t.id === lockedTo) : undefined;
    if (own) {
      tenantTitle.textContent = own.name;
      tenantTitle.classList.remove('hidden');
      brandTag.textContent = 'integrator';
    } else {
      tenantTitle.classList.add('hidden');
      brandTag.textContent = 'admin';
    }
  }
  const integratorBadge = document.getElementById('integrator-badge');
  const roleBadge = document.getElementById('role-badge');
  if (integratorBadge) {
    // Only meaningful in integrator scope — in provider scope there is no active
    // tenant, so the chip would otherwise show a stale last-selected slug.
    const scope = deriveScope(currentPath());
    const tid = scope.mode === 'integrator' ? scope.integratorId : null;
    const integrator = tid ? integrators.find((t) => t.id === tid) : undefined;
    if (integrator) {
      integratorBadge.textContent = integrator.slug;
      integratorBadge.classList.remove('hidden');
    } else {
      integratorBadge.classList.add('hidden');
    }
  }
  if (roleBadge) {
    if (roles.length > 0) {
      roleBadge.textContent = roles.join(' / ');
      roleBadge.classList.remove('hidden');
    } else {
      roleBadge.classList.add('hidden');
    }
  }
}

function applySidebarPermissions(): void {
  document.querySelectorAll<HTMLAnchorElement>('.sidebar a[data-route]').forEach((el) => {
    const route = el.dataset.route!;
    // Identity-gated routes short-circuit the permission lookup entirely.
    if (SUPERADMIN_ROUTES.has(route)) {
      el.classList.toggle('hidden', !(session?.isSuperAdmin ?? false));
      return;
    }
    const required = SIDEBAR_GUARDS[route];
    if (!required) return;
    if (!principalHasPermission(required)) {
      el.classList.add('hidden');
    } else {
      el.classList.remove('hidden');
    }
  });
  hideEmptySidebarGroups();
}

/**
 * A subgroup heading whose every link has been permission-gated away is a
 * label over nothing — it reads as a section that failed to load rather than
 * one the principal simply has no access to. Hide the heading with its body.
 */
function hideEmptySidebarGroups(): void {
  document.querySelectorAll<HTMLElement>('.sidebar .sidebar-subgroup-body').forEach((body) => {
    const links = Array.from(body.querySelectorAll<HTMLAnchorElement>('a'));
    const anyVisible = links.some((a) => !a.classList.contains('hidden'));
    body.classList.toggle('hidden', !anyVisible);
    const heading = body.previousElementSibling;
    if (heading?.classList.contains('sidebar-subgroup')) {
      heading.classList.toggle('hidden', !anyVisible);
    }
  });
}

/**
 * Rewrite hrefs of integrator-scoped sidebar links to point at the current integrator.
 * Also toggles a `.no-integrator` class on the integrator section so CSS can gray it
 * out when no integrator is selected — keeps users from clicking into bail-outs.
 */
function updateSidebarTenantLinks(): void {
  const tid = currentTenantId();
  const section = document.getElementById('sidebar-integrator-section');
  const nameEl = document.getElementById('sidebar-integrator-name');

  if (section) section.classList.toggle('no-integrator', !tid);
  if (nameEl) {
    if (tid) {
      const t = integrators.find((x) => x.id === tid);
      nameEl.textContent = t ? `· ${t.slug}` : '';
    } else {
      nameEl.textContent = '· pick one';
    }
  }

  document.querySelectorAll<HTMLAnchorElement>('a[data-integrator-route]').forEach((a) => {
    const sub = a.dataset.integratorRoute!;
    a.href = tid ? `/integrators/${tid}/${sub}` : '#';
  });
}

/** Reflect the current route's scope into the shell, navs, and switcher. */
function applyScope(): Scope {
  const scope = deriveScope(currentPath());
  const navProvider = document.getElementById('nav-provider');
  const navIntegrator = document.getElementById('nav-integrator');
  // A locked principal always gets the integrator nav, whatever the URL says.
  // Some tenant-editing pages sit on provider-shaped routes — `/games/:gameId`
  // edits the SELECTED integrator's override — so keying the sidebar purely off
  // the route left tenant staff on a real page with an empty sidebar and no way
  // to navigate off it.
  const locked = session?.isLocked ?? false;
  const isIntegrator = scope.mode === 'integrator' || locked;
  if (navProvider) navProvider.classList.toggle('hidden', isIntegrator);
  if (navIntegrator) navIntegrator.classList.toggle('hidden', !isIntegrator);
  // Chrome follows the nav, not the raw route, so a locked principal on a
  // provider-shaped URL still reads as being inside their own tenant.
  document.documentElement.setAttribute('data-scope', isIntegrator ? 'integrator' : 'provider');

  if (scope.mode === 'integrator') {
    // Route is canonical: the picker reflects it (no `change` fired).
    if (els.integratorPicker.value !== scope.integratorId) {
      integratorPicker.setValue(scope.integratorId);
    }
    integratorPicker.setContext({ mode: 'integrator', id: scope.integratorId });
  } else {
    // Provider scope. Game-config routes are tenant-selectable (the picker
    // chooses whose override to edit), so keep the selected tenant there.
    // Every other provider route has no active tenant — clear the stale
    // selection so the picker reads "Provider · all integrators" instead of
    // the last integrator's name.
    if (!currentPath().startsWith('/games/')) {
      integratorPicker.setValue('');
    }
    integratorPicker.setContext({ mode: 'provider' });
  }
  updateSidebarTenantLinks();
  applySidebarPermissions(); // re-hide links the principal lacks perms for in the now-visible nav
  updateTopbarBadges(); // refresh the integrator chip (hidden in provider scope)
  return scope;
}

function showLogin(): void {
  // The banner lives between the topbar and shell; hide it too or it lingers
  // over the login screen after a logout/expiry.
  stopMaintenanceBannerPoll();
  els.login.classList.remove('hidden');
  // Fully hide the app shell — dimming it to 0.3 opacity (the old behaviour)
  // created a stacking context that let the topbar bleed over the login and
  // left stale data (avatar, picker, page) visible behind the form.
  els.topbar.classList.add('hidden');
  els.shell.classList.add('hidden');
  els.topbar.style.opacity = '';
  els.shell.style.opacity = '';
}

function hideLogin(): void {
  els.login.classList.add('hidden');
  els.topbar.classList.remove('hidden');
  els.shell.classList.remove('hidden');
  // Clear any session-expiry banner once the user is back in.
  els.loginBanner.textContent = '';
  els.loginBanner.classList.add('hidden');
}

/**
 * Centralised session-expiry handler. Any 401 (token expired/revoked while the
 * user was working) routes here via the AdminClient `onUnauthorized` hook:
 * drop the dead token, stop background polling, and surface the auth screen
 * with a banner explaining the bounce — instead of pages rendering a raw
 * `UNAUTHORIZED` string. Re-entrant-safe so a burst of parallel 401s only
 * shows the screen once.
 */
function handleSessionExpired(message?: string): void {
  if (!els.login.classList.contains('hidden')) return; // already on auth screen
  session = null;
  client = null;
  if (approvalsBadgeTimer) {
    clearInterval(approvalsBadgeTimer);
    approvalsBadgeTimer = null;
  }
  stopMaintenanceBannerPoll();
  // Only explain a bounce if the user was actually signed in this load.
  // A cold load / post-logout 401 just shows a clean login, no scary banner.
  if (hasAuthenticated) {
    els.loginBanner.textContent =
      message ?? 'Your session expired. Please sign in again to continue.';
    els.loginBanner.classList.remove('hidden');
  }
  hasAuthenticated = false;
  showLogin();
}

async function bootstrap(): Promise<void> {
  // Auth rides the HttpOnly cookie (credentials:'include'). No cookie →
  // /v1/me returns 401 → login screen.
  client = new AdminClient({
    baseUrl: API_BASE,
    onUnauthorized: () => handleSessionExpired(),
  });

  try {
    const me = await client.me();
    session = {
      userId: me.userId,
      email: me.email,
      roles: me.roles.map((r) => r.role),
      permissions: new Set(me.permissions),
      isSuperAdmin: me.isSuperAdmin,
      isLocked: me.isLocked ?? false,
      tenantLock: me.tenantLock ?? [],
    };
    hasAuthenticated = true;
  } catch (err) {
    if (err instanceof AdminApiError && err.status === 401) {
      // No valid session — show login without the "expired" banner on a cold load.
      session = null;
      client = null;
      showLogin();
      return;
    }
    showError(err);
    return;
  }

  try {
    integrators = await client.listIntegrators();
  } catch (err) {
    if (err instanceof AdminApiError && err.status === 401) {
      handleSessionExpired('Your session has ended. Please sign in again.');
      return;
    }
    showError(err);
    return;
  }
  populateTenants();
  const email = session.email ?? 'admin';
  els.who.textContent = email;
  const initials = avatarInitials(email);
  els.acctAvatar.textContent = initials;
  els.acctAvatarLg.textContent = initials;
  applySidebarPermissions();
  // Provider mode needs a GLOBAL grant. isSuperAdmin alone was the old proxy
  // for that, back when every admin-ui user was provider staff; a tenant admin
  // is neither super_admin nor entitled to cross-tenant surfaces.
  const canProvider = !(session?.isLocked ?? false);
  integratorPicker.setProviderEntryVisible(canProvider);
  document.documentElement.classList.toggle('no-provider', !canProvider);
  // A switcher with exactly one option is furniture. The topbar badge already
  // names the tenant, so drop the control rather than render a dropdown that
  // cannot change anything. Kept for the rare principal granted on several
  // integrators, who genuinely does switch.
  document.documentElement.classList.toggle(
    'single-tenant',
    (session?.isLocked ?? false) && (session?.tenantLock.length ?? 0) === 1,
  );
  updateTopbarBadges();
  // A locked principal has no provider scope to land in. Without this they log
  // in to the Provider overview, which immediately 403s — the server refuses
  // correctly, the operator just sees a broken console. Send them to their own
  // tenant instead. Cosmetic only: the server is the boundary.
  if (session?.isLocked && session.tenantLock.length > 0) {
    const home = session.tenantLock[0]!;
    const path = location.pathname;
    // `/games/:gameId` is provider-SHAPED but tenant-scoped in practice: it
    // edits the selected integrator's override, and the picker keeps its
    // selection there (see applyScope). Bouncing off it would strand a tenant
    // admin who cannot reach their own game config any other way.
    const tenantEditable = path.startsWith('/games/');
    if (!tenantEditable && deriveScope(path).mode === 'provider') {
      navigate(`/integrators/${home}/home`, { replace: true });
    }
  }
  // Route is canonical — reflect the current URL's scope into the shell before
  // the first render (navs, picker label, data-scope).
  applyScope();
  hideLogin();
  await routeChanged();
  startApprovalsBadgePoll();
  startMaintenanceBannerPoll();
}

let approvalsBadgeTimer: ReturnType<typeof setInterval> | null = null;
/**
 * Pending-approvals badge.
 *
 * Unlocked (provider) principals read the GLOBAL count, shown on both the
 * provider-nav and integrator-nav Approvals links: the whole point is that
 * pending work outside the currently selected tenant is no longer invisible.
 * Locked principals keep the per-tenant count — they have no other tenant, and
 * the cross-tenant endpoint is closed to them.
 */
async function refreshApprovalsBadge(): Promise<void> {
  if (!client) return;
  const badges = ['sidebar-approvals-badge', 'sidebar-provider-approvals-badge']
    .map((id) => document.getElementById(id))
    .filter((el): el is HTMLElement => el !== null);
  if (badges.length === 0) return;
  // Finance/support hold no config read; the list would only 403 every tick.
  if (!principalHasPermission('game.config.read')) return;
  let count: number;
  try {
    if (session?.isLocked) {
      const tid = currentTenantId();
      if (!tid) return;
      count = (await client.listApprovals(tid, 'pending')).length;
    } else {
      count = await client.countPendingApprovals();
    }
  } catch {
    return; // ignore — next tick retries
  }
  for (const badge of badges) {
    badge.classList.toggle('hidden', count === 0);
    badge.textContent = count > 99 ? '99+' : String(count);
  }
}

function startApprovalsBadgePoll(): void {
  if (approvalsBadgeTimer) clearInterval(approvalsBadgeTimer);
  void refreshApprovalsBadge();
  approvalsBadgeTimer = setInterval(() => void refreshApprovalsBadge(), 30_000);
}

// ── Global maintenance nag banner ────────────────────────────────────────────
let maintBannerTimer: ReturnType<typeof setInterval> | null = null;

function hideMaintBanner(): void {
  els.maintBanner.classList.add('hidden');
  els.maintBanner.innerHTML = '';
}

/** "1h 36m" / "42m" / "just now" from an elapsed millisecond span. */
function fmtElapsed(ms: number): string {
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 1) return 'just now';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

async function refreshMaintenanceBanner(): Promise<void> {
  if (!client) return hideMaintBanner();
  let maintenance;
  try {
    ({ maintenance } = await client.getGlobalMaintenance());
  } catch {
    // Read failed (transient/perms) — leave the banner as-is; next tick retries.
    return;
  }
  if (!maintenance.enabled) return hideMaintBanner();

  // Engaged. Only nag once it's outstayed the threshold — a normal short deploy
  // window shouldn't spam. Missing `since` = unknown duration → warn to be safe.
  const sinceMs = maintenance.since ? Date.parse(maintenance.since) : NaN;
  const elapsedMs = Number.isNaN(sinceMs) ? NaN : Date.now() - sinceMs;
  if (!Number.isNaN(elapsedMs) && elapsedMs < MAINTENANCE_WARN_AFTER_MIN * 60_000) {
    return hideMaintBanner();
  }

  const forLabel = Number.isNaN(elapsedMs)
    ? 'for an unknown duration'
    : `for ${fmtElapsed(elapsedMs)}`;
  els.maintBanner.innerHTML = `
    <span class="maint-banner-ic" aria-hidden="true">&#9888;</span>
    <span class="maint-banner-text">
      <strong>Global maintenance is engaged ${escapeHtml(forLabel)}.</strong>
      New rounds are paused platform-wide for every game and integrator. If the
      deploy is done, lift it to resume play.
    </span>
    <a class="maint-banner-link" href="/maintenance" data-route="maintenance">Review &amp; lift &rarr;</a>
  `;
  els.maintBanner.classList.remove('hidden');
}

function startMaintenanceBannerPoll(): void {
  if (maintBannerTimer) clearInterval(maintBannerTimer);
  // Only admins who can reach the maintenance page get the nag + its deeplink;
  // for everyone else the banner (and its GET) stay dormant.
  if (!principalHasPermission('game.config.write')) {
    hideMaintBanner();
    return;
  }
  void refreshMaintenanceBanner();
  maintBannerTimer = setInterval(() => void refreshMaintenanceBanner(), MAINTENANCE_POLL_MS);
}

function stopMaintenanceBannerPoll(): void {
  if (maintBannerTimer) {
    clearInterval(maintBannerTimer);
    maintBannerTimer = null;
  }
  hideMaintBanner();
}

function populateTenants(): void {
  // Provider integrator is a system baseline row — never shown in the picker
  // as a selectable tenant. Provider scope is accessed via the pinned
  // "Provider · all integrators" entry, not by picking it from the list.
  const realIntegrators = integrators.filter((i) => !i.isProvider);
  integratorPicker.setIntegrators(realIntegrators);
  // Default to provider scope (empty value) so a fresh load lands on the
  // baseline — the picker label ("Provider · all integrators") then matches the
  // actual scope. Only a previously-picked tenant restores integrator scope.
  const stored = localStorage.getItem(SELECTED_TENANT_KEY);
  if (stored && realIntegrators.some((t) => t.id === stored)) {
    integratorPicker.setValue(stored);
  } else {
    // Explicitly select provider scope (empty value). Without this the hidden
    // <select> falls back to its first option, silently landing on the first
    // integrator instead of the baseline.
    integratorPicker.setValue('');
  }
}

function currentTenantId(): string | null {
  const scope = deriveScope(currentPath());
  return scope.mode === 'integrator' ? scope.integratorId : (els.integratorPicker.value || null);
}

function highlightSidebar(route: string): void {
  document.querySelectorAll('.sidebar a').forEach((el) => {
    el.classList.toggle('active', el.getAttribute('data-route') === route);
  });
}

/** Parse `/integrators/:id/<sub>/<tail>` paths. Returns null if not integrator-scoped. */
function parseIntegratorRoute(
  path: string,
): { integratorId: string; sub: string; tail: string } | null {
  if (!path.startsWith('/integrators/')) return null;
  const parts = path.slice('/integrators/'.length).split('/');
  const integratorId = parts[0] ?? '';
  if (!UUID_RE.test(integratorId)) return null;
  const sub = parts[1] ?? '';
  const tail = parts.slice(2).join('/');
  return { integratorId, sub, tail };
}

/** Bounce legacy bare paths like `/reports` to `/integrators/:current/reports`. */
const LEGACY_REDIRECT_PATHS: ReadonlyArray<string> = [
  'home', 'players', 'games', 'game-catalog', 'reports',
  'approvals', 'activity', 'api-keys', 'webhooks',
  'wallet-ledger', 'wallet-drift', 'round-ops',
  'rounds-explorer', 'audit', 'audit-chains', 'transaction-trace',
];

/**
 * Prepend a small integrator-context bar inside the content area after a
 * integrator-scoped page finishes rendering. Carries the integrator name + slug +
 * status pill + a Settings shortcut. Keeps users oriented when picker swap
 * happens mid-task or when bookmarked URLs are opened.
 */
function injectTenantScopeBar(integratorId: string): void {
  // The bar answers "which tenant am I looking at?" — a question only a
  // provider admin, who moves between them, can have. Tenant staff are always
  // in their own integrator, and the topbar already names it, so for them this
  // is a third copy of something they cannot change.
  if (session?.isLocked) return;
  const integrator = integrators.find((t) => t.id === integratorId);
  if (!integrator) return;
  // Don't double-inject if a page re-render fired without clearing.
  if (els.content.querySelector('.integrator-scope-bar')) return;
  const bar = document.createElement('div');
  bar.className = 'integrator-scope-bar';
  bar.innerHTML = `
    <span class="integrator-scope-label">Integrator</span>
    <strong class="integrator-scope-name">${escapeHtml(integrator.name)}</strong>
    <code class="integrator-scope-slug">${escapeHtml(integrator.slug)}</code>
    <span class="pill ${integrator.status === 'active' ? 'live' : 'warn'}">${escapeHtml(integrator.status)}</span>
    <a href="/integrators/${integratorId}/settings" class="integrator-scope-settings">Settings →</a>
  `;
  els.content.insertBefore(bar, els.content.firstChild);
}

async function routeChanged(): Promise<void> {
  if (!client) return;
  const path = currentPath();
  els.content.innerHTML = '<p class="muted">Loading…</p>';

  // Provider-scope routes
  if (path === '/' || path === '/overview') {
    highlightSidebar('overview');
    const deps: OverviewPageDeps = { client };
    await renderOverviewPage(els.content, deps);
    return;
  }

  if (path === '/integrators' || path === '/integrators/') {
    highlightSidebar("integrators");
    const deps: IntegratorsPageDeps = { client };
    await renderIntegratorsPage(els.content, deps);
    return;
  }

  const providerReportView: Record<string, ProviderReportView> = {
    '/reports-by-integrator': 'by-integrator',
    '/reports-by-game': 'by-game',
    '/reports-by-player': 'by-player',
  };
  if (providerReportView[path]) {
    const view = providerReportView[path]!;
    highlightSidebar(path.slice(1)); // 'reports-by-integrator' | …-game | …-player
    const q = new URLSearchParams(window.location.search);
    const deps: ProviderReportDeps = {
      client,
      view,
      integrators,
      ...(q.get('integratorId') ? { drillIntegratorId: q.get('integratorId')! } : {}),
      ...(q.get('gameId') ? { drillGameId: q.get('gameId')! } : {}),
    };
    await renderProviderReportPage(els.content, deps);
    return;
  }

  if (path.startsWith('/admin-users')) {
    highlightSidebar('admin-users');
    const deps: AdminUsersPageDeps = { client, integrators };
    await renderAdminUsersPage(els.content, deps);
    return;
  }

  if (path.startsWith('/currencies')) {
    highlightSidebar('currencies');
    await renderCurrenciesPage(els.content, { client });
    return;
  }

  if (path.startsWith('/system-health')) {
    highlightSidebar('system-health');
    const deps: SystemHealthPageDeps = { client };
    await renderSystemHealthPage(els.content, deps);
    return;
  }

  if (path.startsWith('/redis-monitoring')) {
    highlightSidebar('redis-monitoring');
    // No client-side gate here on purpose: the server is the boundary, and a
    // second copy of the superadmin rule in the router is a drift risk. The
    // page renders a Restricted card on the 403.
    const deps: RedisMonitoringPageDeps = { client };
    await renderRedisMonitoringPage(els.content, deps);
    return;
  }

  // Provider-wide approvals queue. Tenant staff fall through to the legacy
  // redirect below, which lands them on their own integrator's queue.
  if (path === '/approvals' && session && !session.isLocked) {
    highlightSidebar('approvals-all');
    await renderProviderApprovalsPage(els.content, { client, integrators });
    return;
  }

  if (path.startsWith('/bulk-config')) {
    highlightSidebar('bulk-config');
    const batch = new URLSearchParams(window.location.search).get('batch');
    await renderBulkConfigPage(els.content, {
      client,
      integrators,
      userId: session?.userId ?? '',
      isSuperAdmin: session?.isSuperAdmin ?? false,
      can: principalHasPermission,
      ...(batch && UUID_RE.test(batch) ? { focusBatchId: batch } : {}),
    });
    return;
  }

  if (path.startsWith('/maintenance')) {
    highlightSidebar('maintenance');
    const deps: MaintenancePageDeps = { client };
    await renderMaintenancePage(els.content, deps);
    return;
  }

  if (path.startsWith('/audit-anchors')) {
    highlightSidebar('audit-anchors');
    const deps: AuditAnchorsPageDeps = { client };
    await renderAuditAnchorsPage(els.content, deps);
    return;
  }

  if (path === '/jackpot' || path.startsWith('/jackpot')) {
    highlightSidebar('jackpot-overview');
    const deps: JackpotOverviewDeps = { client };
    await renderJackpotOverviewPage(els.content, deps);
    return;
  }

  if (path.startsWith('/inspector-all')) {
    highlightSidebar('inspector-all');
    const deps: InspectorAllPageDeps = {
      client,
      gameId: 'greedyking',
      gatewayUrl: GATEWAY_URL,
      roundEngineBaseUrl: ROUND_ENGINE_BASE,
    };
    await renderInspectorAllPage(els.content, deps);
    return;
  }

  // Provider-scope game library — global config, no integrator scope.
  if (path === '/games' || path === '/games/') {
    highlightSidebar('provider-games');
    const providerIntegrator = integrators.find((i) => i.isProvider) ?? null;
    // Picker-null = "Provider · all integrators" = edit the provider baseline.
    // Route to the provider integrator id, NOT integrators[0].
    const scopeIntegratorId = currentTenantId() ?? providerIntegrator?.id;
    if (!scopeIntegratorId) {
      els.content.innerHTML = '<p class="muted">No provider integrator configured. Run the provider-default migration.</p>';
      return;
    }
    await renderProviderGamesPage(els.content, { client, platformTenantId: scopeIntegratorId });
    return;
  }
  if (path.startsWith('/games/')) {
    const rest = path.slice('/games/'.length).split('/');
    const gameId = rest[0]!;
    const tabRaw = rest[1] ?? null;
    const providerIntegrator = integrators.find((i) => i.isProvider) ?? null;
    const realIntegrators = integrators.filter((i) => !i.isProvider);
    // Picker-null = "Provider · all integrators" = edit the provider baseline.
    // Route to the provider integrator id, NOT integrators[0].
    const scopeIntegratorId = currentTenantId() ?? providerIntegrator?.id;
    if (!scopeIntegratorId) {
      els.content.innerHTML = '<p class="muted">No provider integrator configured. Run the provider-default migration.</p>';
      return;
    }
    const isProviderScope = currentTenantId() === null;
    const integratorLabel = isProviderScope
      ? 'Provider default'
      : (realIntegrators.find((i) => i.id === scopeIntegratorId)?.name ?? scopeIntegratorId);
    highlightSidebar('provider-games');
    await renderGameDetailPage(
      els.content,
      {
        client,
        integratorId: scopeIntegratorId,
        gameId,
        roundEngineBaseUrl: ROUND_ENGINE_BASE,
        gatewayUrl: GATEWAY_URL,
        isProviderScope,
        integratorLabel,
        can: principalHasPermission,
      },
      parseTabFromHash(tabRaw ?? ''),
    );
    return;
  }

  // Global player detail — keeps `/players/:id` as a stable deep link.
  if (path.startsWith('/players/')) {
    const playerId = path.slice('/players/'.length);
    if (!UUID_RE.test(playerId)) {
      els.content.innerHTML = '<p class="error">Invalid player id.</p>';
      return;
    }
    const deps: PlayerDetailPageDeps = { client, playerId, can: principalHasPermission };
    await renderPlayerDetailPage(els.content, deps);
    return;
  }

  // Integrator-scoped routes: /tenants/:id[/<sub>/<tail>]
  const tr = parseIntegratorRoute(path);
  if (tr) {
    const { integratorId, sub, tail } = tr;
    if (!sub) {
      highlightSidebar("integrators");
      const deps: IntegratorDetailPageDeps = {
        client,
        integratorId,
        can: principalHasPermission,
        isLocked: session?.isLocked ?? false,
      };
      await renderIntegratorDetailPage(els.content, deps);
      injectTenantScopeBar(integratorId);
      return;
    }
    await routeTenantSub(integratorId, sub, tail);
    injectTenantScopeBar(integratorId);
    return;
  }

  // Legacy redirects → /tenants/:current/<sub>
  for (const sub of LEGACY_REDIRECT_PATHS) {
    const legacy = `/${sub}`;
    if (path === legacy || path.startsWith(legacy + '/')) {
      const tid = currentTenantId();
      if (!tid) {
        navigate('/', { replace: true });
        return;
      }
      const tail = path.slice(legacy.length);
      navigate(`/integrators/${tid}/${sub}${tail}`, { replace: true });
      return;
    }
  }

  els.content.innerHTML = '<p class="muted">Page not found.</p>';
}

async function routeTenantSub(integratorId: string, sub: string, tail: string): Promise<void> {
  if (!client) return;
  highlightSidebar(sub);

  switch (sub) {
    case 'home': {
      const integrator = integrators.find((t) => t.id === integratorId);
      const deps: HomePageDeps = {
        client,
        integratorId,
        tenantName: integrator?.name ?? integratorId,
        roundEngineBaseUrl: ROUND_ENGINE_BASE,
      };
      await renderHomePage(els.content, deps);
      return;
    }
    case 'staff': {
      await renderTenantStaffPage(els.content, {
        client,
        integratorId,
        currentUserId: session?.userId ?? '',
      });
      return;
    }
    case 'settings': {
      const deps: IntegratorDetailPageDeps = {
        client,
        integratorId,
        can: principalHasPermission,
        isLocked: session?.isLocked ?? false,
      };
      await renderIntegratorDetailPage(els.content, deps);
      return;
    }
    case 'players': {
      if (tail) {
        // /integrators/:id/players/:playerId — player detail inside integrator
        // scope, so the sidebar nav + scope bar stay on this integrator.
        if (!UUID_RE.test(tail)) {
          els.content.innerHTML = '<p class="error">Invalid player id.</p>';
          return;
        }
        const deps: PlayerDetailPageDeps = { client, playerId: tail, integratorId, can: principalHasPermission };
        await renderPlayerDetailPage(els.content, deps);
        return;
      }
      const deps: PlayersPageDeps = { client, integratorId };
      await renderPlayersPage(els.content, deps);
      return;
    }
    case 'games': {
      if (!tail) {
        await renderGamesIndexPage(els.content, { client, integratorId });
        return;
      }
      // Per-game config is global — drill-ins go to the provider-scope
      // route, not a per-integrator one. Catch stale bookmarks here.
      navigate(`/games/${tail}`, { replace: true });
      return;
    }
    case 'game-catalog': {
      // Catalog merged into Games — redirect for any stale bookmark.
      navigate(`/integrators/${integratorId}/games`, { replace: true });
      return;
    }
    case 'reports': {
      const segs = tail.split('/').filter(Boolean);
      const firstSeg = segs[0] ?? '';

      // By game / by player are the same page as the provider console's, held
      // to this one integrator. Worth having here rather than a tenant-only
      // rewrite: the currency-split layout is the most readable view we have of
      // betting economics, and it is exactly what an integrator's finance staff
      // are trying to read. The server applies the lock regardless — passing
      // the id keeps a *provider* who drilled in scoped to the tenant they are
      // looking at, instead of silently showing them every integrator.
      const scopedView: Record<string, ProviderReportView> = {
        'by-game': 'by-game',
        'by-player': 'by-player',
      };
      if (scopedView[firstSeg]) {
        highlightSidebar(`reports-${firstSeg}`);
        const q = new URLSearchParams(window.location.search);
        const deps: ProviderReportDeps = {
          client,
          view: scopedView[firstSeg]!,
          integrators,
          drillIntegratorId: integratorId,
          scopedToIntegrator: true,
          ...(q.get('gameId') ? { drillGameId: q.get('gameId')! } : {}),
        };
        await renderProviderReportPage(els.content, deps);
        return;
      }

      const validTabs: ReadonlyArray<ReportTab> = ['pl', 'product-pl', 'player-pl', 'game-log', 'retention', 'reconcile'];
      const tab: ReportTab = (validTabs as ReadonlyArray<string>).includes(firstSeg)
        ? (firstSeg as ReportTab)
        : 'pl';
      // Product P/L + Player P/L folded into P/L — old links highlight P/L.
      const navTab = tab === 'product-pl' || tab === 'player-pl' ? 'pl' : tab;
      highlightSidebar(`reports-${navTab}`);
      // Per-game drill-down: /reports/(pl|product-pl)/:gameId → game breakdown.
      const gameId = segs[1] ? decodeURIComponent(segs[1]) : '';
      if (gameId && (tab === 'pl' || tab === 'product-pl')) {
        await renderReportGameDetailPage(els.content, {
          client,
          integratorId,
          gameId,
          backTab: tab,
        });
        return;
      }
      const deps: ReportsPageDeps = { client, integratorId, tab };
      await renderReportsPage(els.content, deps);
      return;
    }
    case 'approvals': {
      const deps: ApprovalsPageDeps = { client, integratorId, isLocked: session?.isLocked ?? false };
      await renderApprovalsPage(els.content, deps);
      return;
    }
    case 'activity': {
      const deps: ActivityPageDeps = { client, integratorId };
      await renderActivityPage(els.content, deps);
      return;
    }
    case 'api-keys': {
      const deps: ApiKeysPageDeps = { client, integratorId };
      await renderApiKeysPage(els.content, deps);
      return;
    }
    case 'webhooks': {
      const deps: WebhooksPageDeps = { client, integratorId };
      await renderWebhooksPage(els.content, deps);
      return;
    }
    case 'wallet-ledger': {
      const deps: WalletLedgerPageDeps = { client, integratorId };
      await renderWalletLedgerPage(els.content, deps);
      return;
    }
    case 'wallet-drift': {
      const deps: WalletDriftPageDeps = { client, integratorId };
      await renderWalletDriftPage(els.content, deps);
      return;
    }
    case 'round-ops': {
      const deps: RoundOpsPageDeps = { client, integratorId };
      await renderRoundOpsPage(els.content, deps);
      return;
    }
    case 'rounds-explorer': {
      // "Round explorer" and "Incomplete games" share this route — the latter
      // adds ?status=open (or ?incomplete=1). Highlight the matching sidebar
      // item so clicking "Incomplete games" doesn't light up "Round explorer".
      const sp = new URLSearchParams(window.location.search);
      const isIncomplete = sp.get('status') === 'open' || sp.get('incomplete') === '1';
      highlightSidebar(isIncomplete ? 'incomplete-games' : 'rounds-explorer');
      const deps: RoundsExplorerDeps = {
        client,
        integratorId,
        roundEngineBaseUrl: ROUND_ENGINE_BASE,
      };
      await renderRoundsExplorerPage(els.content, deps);
      return;
    }
    case 'audit': {
      const deps: AuditPageDeps = { client, integratorId };
      await renderAuditPage(els.content, deps);
      return;
    }
    case 'audit-chains': {
      const deps: AuditChainsPageDeps = { client };
      await renderAuditChainsPage(els.content, deps);
      return;
    }
    case 'transaction-trace': {
      const deps: TransactionTraceDeps = { client, integratorId, roundEngineBaseUrl: ROUND_ENGINE_BASE };
      renderTransactionTracePage(els.content, deps);
      return;
    }
    default:
      els.content.innerHTML = '<p class="muted">Page not found.</p>';
  }
}

function showError(err: unknown): void {
  const msg = err instanceof Error ? err.message : String(err);
  els.content.innerHTML = `<div class="card"><h2>Error</h2><p class="error">${escapeHtml(msg)}</p></div>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}

// Wire UI events
async function loginWithCredentials(): Promise<void> {
  const email = els.loginEmail.value.trim();
  const password = els.loginPassword.value;
  if (!email || !password) {
    els.loginError.textContent = 'Email + password required';
    return;
  }
  els.loginError.textContent = 'Signing in…';
  try {
    const res = await fetch(`${API_BASE}/v1/auth/login`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include', // store the Set-Cookie session
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      els.loginError.textContent = body.error ?? `HTTP ${res.status}`;
      return;
    }
    const body = (await res.json()) as {
      token?: string;
      requireMfa?: boolean;
      mfaTicket?: string;
    };
    // Two-factor gate: server returned a short-lived ticket instead of
    // the final JWT. Prompt for the 6-digit TOTP (or a backup code) and
    // POST to /v1/auth/mfa/verify to complete login.
    if (body.requireMfa && body.mfaTicket) {
      const code = window.prompt(
        'Enter your 6-digit authenticator code (or a backup code):',
      );
      if (!code) {
        els.loginError.textContent = 'MFA code required.';
        return;
      }
      const vres = await fetch(`${API_BASE}/v1/auth/mfa/verify`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include', // store the Set-Cookie session
        body: JSON.stringify({ mfaTicket: body.mfaTicket, code: code.trim() }),
      });
      if (!vres.ok) {
        const vbody = (await vres.json().catch(() => ({}))) as { error?: string };
        els.loginError.textContent = vbody.error ?? `HTTP ${vres.status}`;
        return;
      }
      const vbody = (await vres.json()) as {
        token: string;
        backupCodeConsumed?: boolean;
        backupCodesRemaining?: number;
      };
      if (vbody.backupCodeConsumed) {
        window.alert(
          `Backup code consumed. ${vbody.backupCodesRemaining ?? 0} remaining. Regenerate from Security settings when low.`,
        );
      }
      // Cookie set by the server; no token kept in JS.
      els.loginEmail.value = '';
      els.loginPassword.value = '';
      els.loginError.textContent = '';
      void bootstrap();
      return;
    }
    // Session cookie set by the server; ride it via bootstrap().
    els.loginEmail.value = '';
    els.loginPassword.value = '';
    els.loginError.textContent = '';
    void bootstrap();
  } catch (err) {
    els.loginError.textContent = err instanceof Error ? err.message : String(err);
  }
}

els.loginSubmit.addEventListener('click', () => void loginWithCredentials());
els.loginPassword.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') void loginWithCredentials();
});
els.loginEmail.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') els.loginPassword.focus();
});

els.loginPwToggle.addEventListener('click', () => {
  const show = els.loginPassword.type === 'password';
  els.loginPassword.type = show ? 'text' : 'password';
  els.loginPwToggle.textContent = show ? 'Hide' : 'Show';
  els.loginPwToggle.setAttribute('aria-pressed', String(show));
  els.loginPwToggle.setAttribute('aria-label', show ? 'Hide password' : 'Show password');
  els.loginPassword.focus();
});

els.logout.addEventListener('click', () => {
  closeAcctMenu();
  void client?.logout().catch(() => {}); // best-effort cookie clear
  session = null;
  client = null;
  hasAuthenticated = false; // explicit logout → no "expired" banner
  els.loginBanner.textContent = '';
  els.loginBanner.classList.add('hidden');
  showLogin();
});

// ── Account menu (avatar → popup) ────────────────────────────────────────────
// Collapses role, email, theme toggle, and logout behind one avatar button.
els.logout.querySelector('.acct-item-ic')!.innerHTML = icon('logout', { size: 16 });

function openAcctMenu(): void {
  els.acctMenu.classList.remove('hidden');
  els.acctToggle.setAttribute('aria-expanded', 'true');
}
function closeAcctMenu(): void {
  els.acctMenu.classList.add('hidden');
  els.acctToggle.setAttribute('aria-expanded', 'false');
}
els.acctToggle.addEventListener('click', (ev) => {
  ev.stopPropagation();
  if (els.acctMenu.classList.contains('hidden')) openAcctMenu();
  else closeAcctMenu();
});
// Clicks inside the menu shouldn't close it (theme toggle stays open); outside does.
els.acctMenu.addEventListener('click', (ev) => ev.stopPropagation());
document.addEventListener('click', () => closeAcctMenu());
document.addEventListener('keydown', (ev) => {
  if (ev.key === 'Escape' && !els.acctMenu.classList.contains('hidden')) {
    closeAcctMenu();
    els.acctToggle.focus();
  }
});

/** Two-letter avatar initials from an email's local part (e.g. admin@… → "AD"). */
function avatarInitials(email: string): string {
  const local = email.split('@')[0] ?? email;
  const parts = local.split(/[._-]+/).filter(Boolean);
  const chars =
    parts.length >= 2 ? parts[0]![0]! + parts[1]![0]! : local.slice(0, 2);
  return chars.toUpperCase();
}

/**
 * Picker change rewrites the integrator segment in the current URL when the user
 * is on a integrator-scoped path. Preserves the subpath + query so "same report,
 * different integrator" is a single click without losing filters.
 */
els.integratorPicker.addEventListener('change', () => {
  const newTid = els.integratorPicker.value;
  localStorage.setItem(SELECTED_TENANT_KEY, newTid);
  pushRecentIntegrator(newTid);
  if (session) updateTopbarBadges();
  updateSidebarTenantLinks();

  const path = currentPath();
  const tr = parseIntegratorRoute(path);
  if (tr) {
    // Already integrator-scoped: swap the tenant in place, keeping the subpath
    // + query so it reads as "same view, different integrator".
    const tail = tr.sub ? `/${tr.sub}${tr.tail ? '/' + tr.tail : ''}` : '';
    navigate(`/integrators/${newTid}${tail}${window.location.search}`);
  } else if (newTid && !path.startsWith('/games/')) {
    // Picking a tenant from a provider route is a context switch: enter that
    // integrator's scope so the sidebar nav, shell accent, and content all
    // refocus on it. (Game-config pages are provider-global — the picker there
    // selects whose override to edit, so they stay put and fall through below.)
    navigate(`/integrators/${newTid}/home`);
  } else {
    // Provider scope (empty value), or a game-config page: re-render in place.
    void routeChanged();
  }
  void refreshApprovalsBadge();
});

onRouteChange(() => {
  applyScope();
  void routeChanged();
});

// Public for inter-module access (timing page needs to re-trigger after approve)
declare global {
  interface Window {
    __reloadRoute: () => Promise<void>;
  }
}
window.__reloadRoute = routeChanged;

// Re-export for the timing page (avoids passing the type guard from each module)
export type { TimingOverride };
export { timingSchema };

installPalette({
  client: () => client,
  integrators: () => integrators,
  currentTenantId: () => currentTenantId(),
  isSuperAdmin: () => session?.isSuperAdmin ?? false,
});
installShortcuts();
installTheme();
installNavInterceptor();
installSidebarCollapse();
installLoginBrandDomain();

void bootstrap();

/**
 * The login panel footer shows the platform's public domain. Derive it from the
 * actual host (admin.club78.net → club78.net, admin.apexsaga.net → apexsaga.net)
 * so the same build is brand-correct on every deployment, instead of hardcoding
 * one domain. Localhost falls back to a neutral label.
 */
function installLoginBrandDomain(): void {
  const foot = document.getElementById('login-aside-foot');
  if (!foot) return;
  const host = location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
  // Strip a leading admin./app./www. subdomain to get the registrable domain.
  const base = host.replace(/^(admin|app|www)\./, '');
  // Audience-neutral: this screen is shown before we know who is signing in,
  // and integrator staff reach it too. Provider-specific framing belongs after
  // authentication, where the principal's scope is known.
  foot.textContent = isLocal ? 'admin console' : base;
}
